<script lang="ts" setup>
import { ref, nextTick, watch } from 'vue';
import { VTextField } from 'vuetify/components/VTextField';
import { Rule } from '../../rules';

type Props = {
  label?: string;
  rules?: Rule[];
  readonly?: boolean;
  errorMessages?: string;
};

withDefaults(defineProps<Props>(), {
  readonly: false,
});
const model = defineModel<string | null>();
const internalModel = ref<string | null>(model.value ?? null);

watch(model, (newText) => {
  internalModel.value = newText ?? null;
});

enum States {
  VIEW,
  EDIT,
}

const state = ref<States>(States.VIEW);
const textField = ref<VTextField>();
const onFocusUpdated = (focused: boolean) => {
  if (!focused) {
    state.value = States.VIEW;
    onUpdateComplete();
  }
};

const onEdit = () => {
  state.value = States.EDIT;
  nextTick(() => {
    textField.value?.focus();
  });
};

const emit = defineEmits<{
  (event: 'blur', value: FocusEvent): void;
  (event: 'editing', value: boolean): void;
  (event: 'update:modelValue', value: string): void;
  (event: 'updated', value: string | null): void;
}>();

watch(state, (newState) => {
  emit('editing', newState === States.EDIT);
});

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    state.value = States.VIEW;
    onUpdateComplete();
  }
};

const onUpdateComplete = () => {
  if (internalModel.value !== model.value) {
    model.value = internalModel.value;
    emit('updated', internalModel.value ?? null);
  }
};
</script>
<template>
  <v-validation :rules="rules" :validation-value="internalModel" v-slot="{ errorMessages, isValid }">
    <div class="flex-grow-1" :class="!isValid ? 'my-1' : 'ma-0'">
      <v-btn
        v-if="state === States.VIEW || readonly"
        class="d-flex justify-start text-body-1 font-weight-regular"
        variant="text"
        size="small"
        @click="onEdit"
        :disabled="readonly"
        :color="isValid ? 'on-surface' : 'error'"
        block
        >{{ model || label }}</v-btn
      >
      <v-text-field
        ref="textField"
        v-if="state === States.EDIT && !readonly"
        v-model="internalModel"
        variant="outlined"
        density="compact"
        block
        :error="!isValid"
        :hide-details="true"
        :readonly="readonly"
        :rules="rules"
        @update:focused="onFocusUpdated"
        @blur="$emit('blur', $event)"
        @keydown="onKeyDown"
      ></v-text-field>

      <div v-if="errorMessages.value.length > 0" role="alert" class="text-error v-messages opacity-100 mx-4">
        {{ errorMessages.value.join(',') }}
      </div>
    </div>
  </v-validation>
</template>
<style scoped lang="scss"></style>
